import { Box } from "@mui/material"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { StandardButton } from "../../../../buttons/StandardButton"
import { FormikTextField } from "../../../../forms/Formik"

import { DynamicFormField, DynamicFormFieldGroupWrap } from "../../form/DynamicFormField"
import { DELIVERY_LOCATION_HAS_OPTIONS_FIELD } from "../../form/formLib"
import { type DynamicConfigField } from "../../form/formTypes"

const useStyles = makeStyles()(() => ({
    buttonLink: {
        textDecoration: "underline",
        "&:hover": {
            background: "transparent",
        },
    },
}))

type OtherDeliveryLocationFieldProps = Readonly<{
    config: DynamicConfigField
    showOtherLocationField: boolean
    setShowOtherLocationField: (v: boolean) => void
    defaultButtonClass?: string
    isMeetingLocation?: boolean
}>

export function OtherDeliveryLocationField({
    config,
    showOtherLocationField,
    setShowOtherLocationField,
    defaultButtonClass,
    isMeetingLocation,
}: OtherDeliveryLocationFieldProps) {
    const { classes, cx } = useStyles()

    const [{ value: deliveryLocationHasOptions }] = useField<boolean | undefined>(DELIVERY_LOCATION_HAS_OPTIONS_FIELD)
    const hasNoDeliveryLocationOptions = deliveryLocationHasOptions === false

    // If there are no predefined delivery locations, this field becomes the only delivery location input
    // and must always be visible.
    if (hasNoDeliveryLocationOptions) {
        const { inputField } = config

        const locationId = isMeetingLocation
            ? "create-order-drawer-meeting-location-field"
            : "create-order-drawer-delivery-location-field"
        const locationLabel = isMeetingLocation ? "Meeting location" : "Delivery location"

        return (
            <DynamicFormFieldGroupWrap config={config}>
                <LocalizedStrict id={locationId} attrs={{ label: true }}>
                    <FormikTextField
                        name={config.key as string}
                        label={locationLabel}
                        fullWidth
                        size="small"
                        multiline={Boolean(inputField?.isMultiline)}
                        inputProps={inputField?.maxLength ? { maxLength: inputField.maxLength } : undefined}
                        InputLabelProps={{ required: true }}
                    />
                </LocalizedStrict>
            </DynamicFormFieldGroupWrap>
        )
    }

    return (
        <>
            {!showOtherLocationField ? (
                <Box>
                    <LocalizedStrict id="create-order-drawer-other-location">
                        <StandardButton
                            type="button"
                            color="blue"
                            variant="text"
                            size="small"
                            fullWidth={false}
                            onClick={() => setShowOtherLocationField(true)}
                            updateDefaultClasses={cx(defaultButtonClass, classes.buttonLink)}
                        >
                            + Other location
                        </StandardButton>
                    </LocalizedStrict>
                </Box>
            ) : (
                <DynamicFormField config={config} />
            )}
        </>
    )
}
